<template lang="pug">
el-form-item.admin-form-field(
  :id="fieldElementId",
  :class="fieldClasses",
  :style="fieldStyle",
  :label="label",
  :prop="prop",
  :required="elFormItemRequired",
  :for="controlId",
  :error="fieldError || undefined"
)
  template(#label)
    span.admin-form-field__label(:id="readonly ? readonlyLabelId : undefined") {{ label }}
  template(v-if="readonly")
    .admin-form-field__value(:aria-labelledby="readonlyLabelId")
      slot
  template(v-else)
    template(v-if="help")
      p.admin-form-field__help(:id="helpId") {{ help }}
    slot(
      :aria-describedby="ariaDescribedby",
      :aria-invalid="Boolean(fieldError)",
      :aria-required="required",
      :disabled="fieldDisabled"
    )
  template(#error="{ error }")
    .el-form-item__error(:id="errorId") {{ error }}
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { adminFormContextKey } from "./types";

const props = withDefaults(
  defineProps<{
    label: string;
    prop?: string;
    controlId?: string;
    required?: boolean;
    allowNull?: boolean;
    help?: string;
    error?: string;
    disabled?: boolean;
    readonly?: boolean;
    span?: "default" | "full";
    fieldClass?: string;
  }>(),
  {
    prop: undefined,
    controlId: undefined,
    required: false,
    allowNull: false,
    help: undefined,
    error: undefined,
    disabled: false,
    readonly: false,
    span: "default",
    fieldClass: undefined,
  },
);

const formContext = inject(adminFormContextKey, undefined);

const fieldError = computed(() => {
  if (props.error !== undefined) {
    return props.error;
  }
  return props.prop ? formContext?.externalErrors.value[props.prop] : undefined;
});

const fieldDisabled = computed(
  () => props.disabled || formContext?.disabled.value === true,
);
// Element Plus adds its own required rule when this is true. That rule rejects
// null, even when the configured registration field explicitly permits null.
const elFormItemRequired = computed(() => props.required && !props.allowNull);

const fieldElementId = computed(() =>
  props.prop
    ? `admin-form-field-${props.prop.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`
    : undefined,
);
const helpId = computed(() =>
  props.controlId ? `${props.controlId}-help` : undefined,
);
const errorId = computed(() =>
  props.prop ? `${fieldElementId.value}-error` : undefined,
);
const readonlyLabelId = computed(() =>
  props.prop
    ? `${fieldElementId.value}-label`
    : `admin-form-field-label-${props.label.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`,
);
const ariaDescribedby = computed(
  () =>
    [helpId.value, fieldError.value ? errorId.value : undefined]
      .filter((id): id is string => id !== undefined)
      .join(" ") || undefined,
);
const fieldClasses = computed(() => [
  props.fieldClass,
  { "is-required": props.required },
]);
const fieldStyle = computed(() =>
  props.span === "full" ? { gridColumn: "1 / -1" } : undefined,
);
</script>

<style scoped>
.admin-form-field {
  align-content: start;
  margin-bottom: 0;
  min-width: 0;
}

.admin-form-field.el-table__row.is-error {
  height: auto;
  min-height: 67px;
}

.admin-form-field__help {
  color: var(--el-text-color-secondary);
  font-size: var(--el-font-size-small);
  margin: 0 0 0.5rem;
}

.admin-form-field__value {
  color: var(--el-text-color-regular);
  font-weight: 700;
  min-width: 0;
  width: 100%;
}

.admin-form-field :deep(.el-form-item__label) {
  align-items: flex-start;
  height: auto;
}

.admin-form-field :deep(.admin-form-field__label) {
  min-height: 40px;
  display: flex;
  align-items: center;
}

.admin-form-field :deep(.el-form-item__content) {
  align-self: flex-start;
  flex-wrap: wrap;
  min-height: 40px;
}

.admin-form-field :deep(.el-form-item__error) {
  box-sizing: border-box;
  contain: inline-size;
  flex-basis: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
  overflow-wrap: anywhere;
  padding-top: 0.25rem;
  position: static;
}
</style>
